import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, configureApiClient, redirectToLogin } from '@/api/client';
import { getAuthStatus, logout as logoutCall } from '@/api/generated';
import type { Me } from '@/api/generated';

// We keep the AuthContext as a "session probe" — small, sync-friendly state
// the auth-spine LoginPage / ProtectedRoute already consume. The richer `/me`
// payload (Bindles, LDAP, hasOnboarded) lives in TanStack Query under
// `['me']` so every component can read it without prop drills.

export const AuthErrorSchema = z.object({
  code: z.enum(['network', 'unexpected', 'unauthenticated']),
  message: z.string(),
});

export type AuthError = z.infer<typeof AuthErrorSchema>;

export interface SessionUser {
  employeeId: string;
  displayName: string;
  email: string | undefined;
}

interface AuthContextType {
  user: SessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  error: AuthError | null;
}

// Exported so test harnesses can construct a fake provider without spinning
// up the full /me round-trip. NOT for production use — components should
// always go through `<AuthProvider>` and `useAuth()`.
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

// Wire interceptors as a module side-effect; idempotent.
configureApiClient();

export const ME_QUERY_KEY = ['me'] as const;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Bypass auth in local development. Seed the ['me'] query cache too —
    // pages like Onboarding read it via queryClient.getQueryData and would
    // otherwise hang on a "Loading your profile…" placeholder.
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      const localMe: Me = {
        employeeId: 'local-dev',
        name: 'Local Developer',
        email: 'developer@example.com',
        ldapGroups: [],
        hasOnboarded: false,
      };
      queryClient.setQueryData<Me>(ME_QUERY_KEY, localMe);
      setUser({
        employeeId: localMe.employeeId,
        displayName: localMe.name,
        email: localMe.email,
      });
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    try {
      const statusResult = await getAuthStatus();
      if ('error' in statusResult && statusResult.error !== undefined) {
        setUser(null);
        setIsAuthenticated(false);
        setError({ code: 'unauthenticated', message: 'Session expired' });
        return;
      }
      const status = statusResult.data;
      if (!status?.authenticated) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      // Synthesize Me from AuthStatus and seed the ['me'] query cache.
      // The dedicated /me route is not yet shipped on the backend — calling
      // it on beta returns 403 from API Gateway and breaks the SPA boot in
      // a redirect loop. AuthStatus carries employeeId (always) and
      // optionally name/email; the remaining Me fields default to empty so
      // consumers (Onboarding, etc.) can render.
      if (!status.employeeId) {
        throw new ApiError('AuthStatus missing identity fields', 500);
      }
      const me: Me = {
        employeeId: status.employeeId,
        name: status.name ?? status.employeeId,
        ...(status.email !== undefined ? { email: status.email } : {}),
        ldapGroups: [],
        hasOnboarded: false,
      };
      queryClient.setQueryData<Me>(ME_QUERY_KEY, me);

      setUser({
        employeeId: me.employeeId,
        displayName: me.name,
        email: status.email,
      });
      setIsAuthenticated(true);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        // Already redirected by the response interceptor — no need to
        // flash an error.
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      const parsed = AuthErrorSchema.safeParse({
        code: 'network',
        message:
          err instanceof Error
            ? err.message
            : 'Network error while checking authentication',
      });
      setError(
        parsed.success
          ? parsed.data
          : { code: 'unexpected', message: 'Unknown auth error' },
      );
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    redirectToLogin();
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    try {
      await logoutCall();
    } catch {
      // Best-effort; we still clear local state.
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });

      const cookiesToClear = ['wayne_ws_token', 'wayne_csrf_token'];
      for (const name of cookiesToClear) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; Secure; SameSite=Strict`;
      }
    }
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkAuth,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { Me };
